import * as React from "react";
import { useState } from "react";
import { styled, Theme } from "@mui/material";
import { Feature } from "fe-common/graphql";
import { URGENCY_DATA } from "fe-web-components/data/urgencyData";
import { Urgency } from "shared";
import { PatientColorMap } from "../../layout/patientColorMap";
import { GenericButton } from "../uiControllers/genericButton";
import { WhatsNextOptionsIndex } from "./useWhatsNextOptions";
import { WhatsNextOptionId } from "./WhatsNext/WhatsNextOption";
import { useTranslate } from "../../hooks/useTranslate";
import { useClinicData } from "../../clinic/clinicHooks";
import { useWidgetSettings } from "../../widget/useWidgetSettings";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { useConversationDriver } from "../../driver/ConversationDriverProvider";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";

//region [[ Styles ]]

const AskDoctorAsButtonView = styled("div")(
  ({ theme, color }: { theme?: Theme; color: string }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& .generic-button, & .generic-button:hover, & .generic-button:active, & .clicked.generic-button, & .clicked.generic-button:hover, & .clicked.generic-button:active": {
      background: color,
      color: PatientColorMap.White,
      borderRadius: "5px",
      width: "100%",
      display: "flex",
      "& span": {
        flexFlow: "column-reverse",
      },
      "& #loading": {
        marginTop: "1px",
      },
    },
    margin: "20px 36px",
  }),
);

//endregion [[ Styles ]]

//region [[ Props ]]

export enum ClinicCtaActionType {
  Url = "url",
  Component = "component",
}

export type ClinicCtaParams = {
  caseId: string;
  clinicId: string;
  partner: string;
  urgency: string;
  apiUrl: string;
};

export interface ClinicCta {
  id: string;
  // Enum (usually WhatsNextOptionId, but not necessarily) in case of component
  // or url in case of external option while containing {OP} that would be translated
  // to any valid filed from clinicSetting
  action: string;
  actionType: ClinicCtaActionType;
}

export interface AskDoctorAsButtonProps {
  feature: Feature;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const AskDoctorAsButton = ({
  feature,
  ...props
}: AskDoctorAsButtonProps) => {
  const { clinic, clinicSettings } = useClinicData();

  const driver = useConversationDriver();

  const { partnerId } = useWidgetSettings();

  const ctaOption = clinicSettings?.cta?.find?.(
    (op) => op.id === "askDoctorAsButton",
  ) as ClinicCta;

  const units = feature?.range?.low || 1;

  const urgencyDataByRange = Object.values(URGENCY_DATA).find(
    (data) => data.selectorRange[0] <= units && units <= data.selectorRange[1],
  );

  const urgencyData = urgencyDataByRange || URGENCY_DATA[Urgency.NOT_URGENT];

  // todo: move all this cta handling to a hook
  const [loading, setLoading] = useState(false);

  const dao = useDaoContext();

  const getUrlFromCta = async (cta: ClinicCta) => {
    const caseId =
      driver.getCaseId() ||
      (await dao.saveConversationAsNew(driver.getCurrentContext(), true));
    const ctaParams: ClinicCtaParams = {
      apiUrl: encodeURIComponent(
        `https://api.kahun.com/status/${caseId ||
          ""}/${clinic}?pid=${partnerId || ""}`,
      ),
      partner: encodeURIComponent(partnerId || ""),
      caseId: encodeURIComponent(caseId || ""),
      urgency: urgencyDataByRange.level.toString(),
      clinicId: encodeURIComponent(clinic || ""),
    };
    return Object.keys(ctaParams).reduce((acc, currentKey) => {
      return acc.replace(`{${currentKey}}`, ctaParams[currentKey]);
    }, cta.action);
  };

  const [open, setOpen] = useState(false);

  const { translate } = useTranslate();

  const talkToDocOption = WhatsNextOptionsIndex.find(
    (o) => o.name === WhatsNextOptionId.DoctorChat,
  );

  const handleOpen = () => {
    if (!ctaOption || ctaOption.actionType === ClinicCtaActionType.Component) {
      setOpen(true);
      return;
    }
    setLoading(true);
    getUrlFromCta(ctaOption).then((url) => {
      setLoading(false);
      window.open(url, "_blank");
    });
  };

  const handleClose = () => setOpen(false);

  if (!talkToDocOption) return null;

  return (
    <AskDoctorAsButtonView color={urgencyData.color}>
      <GenericButton
        onClick={handleOpen}
        label={translate(talkToDocOption.displayText)}
        icon={
          loading ? <ThreeDotsLoader color={PatientColorMap.Primary} /> : null
        }
      />
      <talkToDocOption.component open={open} onClose={handleClose} />
    </AskDoctorAsButtonView>
  );
};
